import { Document, Image, Link, Page, Path, StyleSheet, Svg, Text, View } from '@react-pdf/renderer';
import bluwaveLogo from '../assets/BluWave-logo_final.png';
import bluwaveLogoWhite from '../assets/whiteBWLogo.png';
import PdfBannerBackdrop, { PDF_BANNER_HEIGHT } from './PdfBannerBackdrop';
import { BRAND_FONT_FAMILY, BRAND_GOLD, BRAND_PRIMARY } from '../utils/pdfBrand';
import { renderRichHtml, richTextStyles } from '../utils/pdfRichText';
import type { ProjectCriteriaFitRow } from './OnePagerPdf';
import {
  BookOpenIcon,
  BriefcaseIcon,
  CalendarIcon,
  ClockIcon,
  DollarSignIcon,
  FileTextIcon,
  Link2Icon,
  MapPinIcon,
} from './pdfIcons';

export type InterimOnePagerPdfProps = {
  fullName: string;
  tagline?: string;
  location?: string;
  linkedinUrl?: string;
  resumeUrl?: string;

  magicLinkUrl?: string;
  inviteTeamMemberUrl?: string;

  generalBioHtml?: string;
  snapshotTitle: string;
  snapshotHtml?: string;
  whyWeTitle: string;
  whyWeHtml?: string;

  projectCriteriaFitTitle?: string;
  projectCriteriaFitRows?: ProjectCriteriaFitRow[];

  startDate?: string;
  onsiteCommitment?: string;
  engagementRate?: string;
};

const styles = StyleSheet.create({
  page: {
    paddingTop: 32,
    paddingBottom: 70,
    paddingHorizontal: 36,
    fontSize: 11,
    color: '#111827',
    // Body copy stays sans (per the 2026 redesign); headings use Roboto Slab.
    fontFamily: 'Helvetica',
  },
  section: {
    marginBottom: 18,
  },
  sectionLast: {
    marginBottom: 0,
  },
  // Full-bleed banner (BPP-231): negative margins cancel the page padding so the
  // backdrop runs edge to edge; content keeps the page's 36pt horizontal rhythm.
  headerBanner: {
    position: 'relative',
    marginTop: -32,
    marginHorizontal: -36,
    marginBottom: 18,
    height: PDF_BANNER_HEIGHT,
    backgroundColor: '#0C1930',
    paddingTop: 20,
    paddingHorizontal: 36,
  },
  headerBrandRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  headerBrandLogo: {
    height: 16,
    width: 84,
    objectFit: 'contain',
  },
  headerTopRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  headerTitleBlock: {
    flex: 1,
    paddingRight: 12,
  },
  headerName: {
    fontSize: 22,
    fontWeight: 700,
    fontFamily: BRAND_FONT_FAMILY,
    color: '#FFFFFF',
    maxLines: 1,
    textOverflow: 'ellipsis',
  },
  headerTagline: {
    marginTop: 4,
    fontSize: 11,
    color: 'rgba(255,255,255,0.88)',
    maxLines: 2,
    textOverflow: 'ellipsis',
  },
  headerButtonsRow: {
    flexDirection: 'row',
    flexShrink: 0,
    gap: 6,
  },
  inviteButton: {
    borderWidth: 1,
    borderColor: '#FFFFFF',
    borderRadius: 6,
    paddingVertical: 7,
    paddingHorizontal: 10,
  },
  inviteButtonText: {
    color: '#FFFFFF',
    fontSize: 9,
    fontWeight: 700,
  },
  introButton: {
    backgroundColor: '#FFFFFF',
    borderRadius: 6,
    paddingVertical: 7,
    paddingHorizontal: 10,
  },
  introButtonText: {
    color: '#132543',
    fontSize: 9,
    fontWeight: 700,
  },
  chipRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 12,
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    borderWidth: 1,
    // Solid white: react-pdf renders rgba() border colors with a green cast.
    borderColor: '#FFFFFF',
    borderRadius: 12,
    paddingVertical: 5,
    paddingHorizontal: 10,
  },
  chipText: {
    fontSize: 10,
    fontWeight: 500,
    color: '#FFFFFF',
  },
  overviewCard: {
    backgroundColor: '#DCEDF7',
    borderRadius: 12,
    paddingTop: 14,
    paddingBottom: 12,
    paddingHorizontal: 16,
  },
  overviewTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 7,
    marginBottom: 8,
  },
  overviewTitle: {
    ...richTextStyles.h2,
    marginBottom: 0,
  },
  availabilityBox: {
    borderWidth: 1,
    borderColor: 'rgba(171,145,81,.45)',
    borderRadius: 12,
    overflow: 'hidden',
  },
  availabilityHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 7,
    backgroundColor: 'rgba(171,145,81,.18)',
    paddingVertical: 10,
    paddingHorizontal: 16,
  },
  availabilityHeaderText: {
    fontSize: 12,
    fontWeight: 700,
    fontFamily: BRAND_FONT_FAMILY,
    color: BRAND_PRIMARY,
  },
  availabilityCellsRow: {
    flexDirection: 'row',
  },
  availabilityCell: {
    flex: 1,
    paddingVertical: 14,
    paddingHorizontal: 16,
    borderRightWidth: 1,
    borderRightColor: 'rgba(171,145,81,.28)',
  },
  availabilityCellLast: {
    borderRightWidth: 0,
  },
  availabilityLabelRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    marginBottom: 6,
  },
  availabilityLabel: {
    fontSize: 9,
    fontWeight: 700,
    color: BRAND_GOLD,
  },
  availabilityValue: {
    fontSize: 12,
    fontWeight: 700,
    color: BRAND_PRIMARY,
  },
  feesBox: {
    borderWidth: 1,
    borderColor: '#E2E2E2',
    borderRadius: 12,
    paddingVertical: 16,
    paddingHorizontal: 16,
  },
  feesLabelRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    marginBottom: 8,
  },
  feesLabel: {
    fontSize: 9,
    fontWeight: 700,
    color: '#60667C',
  },
  feesValue: {
    fontSize: 20,
    fontWeight: 700,
    fontFamily: BRAND_FONT_FAMILY,
    color: BRAND_PRIMARY,
  },
  tableHeaderCell: {
    fontSize: 11,
    fontWeight: 700,
    fontFamily: BRAND_FONT_FAMILY,
    color: BRAND_PRIMARY,
  },
  criteriaHeader: {
    flexDirection: 'row',
    borderBottomWidth: 2,
    borderBottomColor: BRAND_PRIMARY,
    paddingVertical: 8,
    paddingHorizontal: 10,
    marginBottom: 6,
  },
  criteriaRow: {
    flexDirection: 'row',
    paddingBottom: 6,
    marginBottom: 6,
    borderBottomWidth: 1,
    borderBottomColor: '#F3F4F6',
  },
  criteriaParagraph: {
    fontSize: 10,
    lineHeight: 1.25,
    marginBottom: 4,
  },
  cellLabel: {
    width: 110,
    paddingRight: 8,
  },
  cellNeeds: {
    flex: 1.35,
    paddingRight: 10,
  },
  cellArrow: {
    width: 24,
    alignItems: 'center',
    paddingTop: 2,
  },
  cellFit: {
    flex: 1.35,
  },
  cellLabelText: {
    fontSize: 11,
    fontWeight: 700,
    fontFamily: BRAND_FONT_FAMILY,
    color: BRAND_PRIMARY,
    lineHeight: 1.1,
  },
  criteriaLabelRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 6,
  },
  criteriaIndexCircle: {
    width: 14,
    height: 14,
    borderRadius: 999,
    backgroundColor: BRAND_GOLD,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 1,
  },
  criteriaIndexText: {
    fontSize: 8,
    fontWeight: 700,
    color: '#FFFFFF',
    lineHeight: 1,
  },
  criteriaArrowCircle: {
    width: 16,
    height: 16,
    borderRadius: 999,
    backgroundColor: '#EEE9DC',
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: -10,
  },
  criteriaArrowSvg: {
    width: 12,
    height: 10,
  },
  divider: {
    marginTop: 12,
    height: 2,
    width: '100%',
    backgroundColor: '#E8E8E8',
  },
  footerWrap: {
    position: 'absolute',
    left: 36,
    right: 36,
    bottom: 20,
  },
  footerDivider: {
    width: '100%',
    height: 2,
    backgroundColor: '#D1D5DB',
  },
  footerLogoRow: {
    width: '100%',
    paddingTop: 10,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  footerDisclaimer: {
    fontSize: 7,
    color: '#9CA3AF',
    flex: 1,
    paddingRight: 12,
  },
  footerLogo: {
    height: 18,
    width: 90,
    objectFit: 'contain',
  },
});

const toTwoLineLabel = (label: string) => {
  const parts = label
    .split(/\s+/)
    .map((p) => p.trim())
    .filter((p) => !!p);
  if (parts.length <= 1) return label;
  const mid = Math.ceil(parts.length / 2);
  return `${parts.slice(0, mid).join(' ')}\n${parts.slice(mid).join(' ')}`;
};

export default function InterimOnePagerPdf(props: InterimOnePagerPdfProps) {
  const criteriaRows = props.projectCriteriaFitRows ?? [];

  // The Project Criteria Fit heading and its column-header row. Kept together with the first
  // criteria row below so the heading can't be stranded at a page break (BPP-380).
  const criteriaTitleAndHeaders = (
    <>
      <Text style={[richTextStyles.h2, { marginBottom: 12 }]}>
        {props.projectCriteriaFitTitle || 'Project Criteria Fit'}
      </Text>

      <View style={styles.criteriaHeader}>
        <View style={styles.cellLabel}>
          <Text style={styles.tableHeaderCell}>Criteria</Text>
        </View>
        <View style={styles.cellNeeds}>
          <Text style={styles.tableHeaderCell}>Your Project Needs</Text>
        </View>
        <View style={styles.cellArrow} />
        <View style={styles.cellFit}>
          <Text style={styles.tableHeaderCell}>Provider Fit</Text>
        </View>
      </View>
    </>
  );

  return (
    <Document>
      <Page size="LETTER" style={styles.page}>
        <View style={styles.footerWrap} fixed>
          <View style={styles.footerDivider} />
          <View style={styles.footerLogoRow}>
            <Text style={styles.footerDisclaimer}>
              Derived from BluWave's knowledge of the provider. Not liable for inaccuracies — independent verification recommended.
            </Text>
            <Image src={bluwaveLogo} style={styles.footerLogo} />
          </View>
        </View>

        <View style={styles.headerBanner} wrap={false}>
          <PdfBannerBackdrop />
          <View style={styles.headerBrandRow}>
            <Image src={bluwaveLogoWhite} style={styles.headerBrandLogo} />
            {(!!props.magicLinkUrl || !!props.inviteTeamMemberUrl) && (
              <View style={styles.headerButtonsRow} wrap={false}>
                {!!props.inviteTeamMemberUrl && (
                  <Link src={props.inviteTeamMemberUrl} style={{ textDecoration: 'none' }}>
                    <View style={styles.inviteButton}>
                      <Text style={styles.inviteButtonText}>Invite Team Member</Text>
                    </View>
                  </Link>
                )}
                {!!props.magicLinkUrl && (
                  <Link src={props.magicLinkUrl} style={{ textDecoration: 'none' }}>
                    <View style={styles.introButton}>
                      <Text style={styles.introButtonText}>Get Introduced</Text>
                    </View>
                  </Link>
                )}
              </View>
            )}
          </View>
          <View style={styles.headerTopRow}>
            <View style={styles.headerTitleBlock}>
              <Text style={styles.headerName}>{props.fullName || 'Provider'}</Text>
              {!!props.tagline && <Text style={styles.headerTagline}>{props.tagline}</Text>}
            </View>
          </View>
          <View style={styles.chipRow}>
            {!!props.location && (
              <View style={styles.chip}>
                <MapPinIcon size={10} color="#FFFFFF" />
                <Text style={styles.chipText}>{props.location}</Text>
              </View>
            )}
            {!!props.linkedinUrl && (
              <Link src={props.linkedinUrl} style={{ textDecoration: 'none' }}>
                <View style={styles.chip}>
                  <Link2Icon size={10} color="#FFFFFF" />
                  <Text style={styles.chipText}>LinkedIn</Text>
                </View>
              </Link>
            )}
            {!!props.resumeUrl && (
              <Link src={props.resumeUrl} style={{ textDecoration: 'none' }}>
                <View style={styles.chip}>
                  <FileTextIcon size={10} color="#FFFFFF" />
                  <Text style={styles.chipText}>Resume</Text>
                </View>
              </Link>
            )}
          </View>
        </View>

        <View style={styles.section}>
          <View style={styles.overviewCard}>
            <View style={styles.overviewTitleRow}>
              <BookOpenIcon size={13} color="#4BABE0" />
              <Text style={styles.overviewTitle}>Executive Overview</Text>
            </View>
            {props.generalBioHtml ? renderRichHtml(props.generalBioHtml, 'overview') : null}
          </View>
        </View>

        <View style={styles.section}>
          <Text style={richTextStyles.h2}>{props.snapshotTitle}</Text>
          {props.snapshotHtml ? renderRichHtml(props.snapshotHtml, 'snapshot') : null}
        </View>

        <View style={styles.section}>
          <Text style={richTextStyles.h2}>{props.whyWeTitle}</Text>
          {props.whyWeHtml ? renderRichHtml(props.whyWeHtml, 'whywe') : null}
        </View>

        {criteriaRows.length > 0 && (
          /*
            No `minPresenceAhead` on this container (it used to be 180): on a section taller
            than a page it never fires, and when the table fits it throws the whole section onto
            the next page, stranding the preceding block. Heading orphans are handled instead by
            keeping the heading + column headers in the same wrap={false} block as the first row.
            The header row is deliberately not `fixed`, which would repeat it on every page. (BPP-380)
          */
          <View style={styles.section}>
            {criteriaRows.map((r, i) => {
              const row = (
                <View key={`${r.label}-${i}`} style={styles.criteriaRow} wrap={false}>
                  <View style={styles.cellLabel}>
                    <View style={styles.criteriaLabelRow}>
                      <View style={styles.criteriaIndexCircle}>
                        <Text style={styles.criteriaIndexText}>{i + 1}</Text>
                      </View>
                      <Text style={styles.cellLabelText}>{toTwoLineLabel(r.label)}</Text>
                    </View>
                  </View>
                  <View style={styles.cellNeeds}>
                    <Text style={styles.criteriaParagraph}>{r.yourNeeds}</Text>
                  </View>
                  <View style={styles.cellArrow}>
                    <View style={styles.criteriaArrowCircle}>
                      <Svg style={styles.criteriaArrowSvg} viewBox="0 0 24 24">
                        <Path
                          d="M5 12h14M13 5l7 7-7 7"
                          stroke={BRAND_GOLD}
                          strokeWidth={2.25}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          fill="none"
                        />
                      </Svg>
                    </View>
                  </View>
                  <View style={styles.cellFit}>
                    {r.providerFitHtml ? (
                      <View>{renderRichHtml(r.providerFitHtml)}</View>
                    ) : (
                      <Text style={styles.criteriaParagraph}>{r.providerFit}</Text>
                    )}
                  </View>
                </View>
              );

              if (i > 0) return row;

              // Keep the heading + column headers with the first row so the heading can't be
              // orphaned at the bottom of a page; if they don't fit, they move together.
              return (
                <View key="criteria-heading" wrap={false}>
                  {criteriaTitleAndHeaders}
                  {row}
                </View>
              );
            })}
          </View>
        )}

        <View style={styles.section}>
          <View style={styles.availabilityBox} wrap={false}>
            <View style={styles.availabilityHeaderRow}>
              <CalendarIcon size={11} color={BRAND_GOLD} />
              <Text style={styles.availabilityHeaderText}>AVAILABILITY</Text>
            </View>
            <View style={styles.availabilityCellsRow}>
              <View style={styles.availabilityCell}>
                <View style={styles.availabilityLabelRow}>
                  <ClockIcon size={9} color={BRAND_GOLD} />
                  <Text style={styles.availabilityLabel}>START DATE</Text>
                </View>
                <Text style={styles.availabilityValue}>{props.startDate || 'Not yet available'}</Text>
              </View>
              <View style={styles.availabilityCell}>
                <View style={styles.availabilityLabelRow}>
                  <BriefcaseIcon size={9} color={BRAND_GOLD} />
                  <Text style={styles.availabilityLabel}>ON-SITE COMMITMENT</Text>
                </View>
                <Text style={styles.availabilityValue}>{props.onsiteCommitment || 'Not yet available'}</Text>
              </View>
              <View style={[styles.availabilityCell, styles.availabilityCellLast]}>
                <View style={styles.availabilityLabelRow}>
                  <MapPinIcon size={9} color={BRAND_GOLD} />
                  <Text style={styles.availabilityLabel}>LOCATION</Text>
                </View>
                <Text style={styles.availabilityValue}>{props.location || 'Not specified'}</Text>
              </View>
            </View>
          </View>
        </View>

        <View style={[styles.section, styles.sectionLast]} wrap={false}>
          <View style={styles.feesBox}>
            <View style={styles.feesLabelRow}>
              <DollarSignIcon size={9} color="#132543" />
              <Text style={styles.feesLabel}>ENGAGEMENT RATE</Text>
            </View>
            <Text style={styles.feesValue}>{props.engagementRate || 'Rate not yet available'}</Text>
          </View>
        </View>
      </Page>
    </Document>
  );
}
